/*
Copyright (c) 2026 Wind River Systems, Inc.

The right to copy, distribute, modify, or otherwise make use of this software may be licensed only pursuant to the terms of an applicable Wind River license agreement.
*/
import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { DownloadHistory, DownloadHistoryParams } from 'src/app/models/products';
import { ProductsService } from 'src/app/services/products/products.service';
import { Subscription } from 'rxjs';

// Dialog box to show the details of one item in My Products.

@Component({
  selector: 'wsn-offline-install-history',
  templateUrl: './offline-install-history.component.html',
  styleUrls: ['./offline-install-history.component.scss']
})
export class OfflineInstallHistoryComponent implements OnInit, OnDestroy {
  private subscription: Subscription;
  requestLogs: DownloadHistory[][];
  requestLogsPage: DownloadHistory[][];
  readonly pageSize = 5;
  pageNumber: number;
  loading = true;
  emptyDataMsg = true;
  treeControl = new NestedTreeControl<any>((node) => node.children);
  dataSource = new MatTreeNestedDataSource<DownloadHistory[]>();
  data: DownloadHistory[][];
  downloadToken: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<OfflineInstallHistoryComponent>,
    @Inject(MAT_DIALOG_DATA) public params: DownloadHistoryParams,
    private productService: ProductsService
  ) {}

  ngOnInit(): void {
    this.subscription = this.productService
      .getDownloadHistory(this.params.uiReleaseId, this.params.userName, this.params.impersonatedUserName)
      .subscribe(
        (response) => {
          const splitListById = response.reduce((groups, item) => {
            if (!groups[item.logId]) {
              groups[item.logId] = [];
            }
            groups[item.logId].push(item);
            return groups;
          }, {});

          this.requestLogs = Object.values(splitListById);
          if (this.requestLogs.length > 0) {
            this.emptyDataMsg = false;
            this.data = this.requestLogs.reverse();
            this.dataSource.data = this.data;
            this.requestLogsPage = this.requestLogs.slice(0, this.pageSize);
            this.handlePageEvent({ pageIndex: 0 } as PageEvent);
          }
          this.loading = false;
        },
        (error) => {
          console.log('error for getting the download history', error);
        }
      );
    this.productService.getOfflineDownloadToken().subscribe((token) => {
      this.downloadToken = token;
    });
  }

  hasChild = (_: number, node: DownloadHistory[]) => node.length > 0;

  downloadUrl(url: string): string {
    return this.downloadToken ? `${url}?token=${this.downloadToken}` : url;
  }

  handlePageEvent(event: PageEvent) {
    this.pageNumber = event.pageIndex;
    const start = this.pageNumber * this.pageSize;
    const end = start + this.pageSize;
    if (this.requestLogs) {
      this.requestLogsPage = this.requestLogs.slice(start, end);
      this.dataSource.data = this.requestLogsPage;
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  close() {
    this.dialogRef.close();
  }
}
